import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./interview-room.page.scss";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import VideoConversation from "../components/video-conversation/video-conversation.component";
import { ProviderContext } from "../providers/providers.provider";
import { Log, ReportModel, RoomStatus } from "../models/report.model";
import CHECK_PURPLE from "../assets/images/check-purple.png";
import FAILED_PURPLE from "../assets/images/failed-purple.png";
import INFO_IMG from "../assets/images/info.png";
import NO_CAMERA_IMG from "../assets/images/camera-off.png";
import { getEmotional, getHardSkill, getRoom, postEmailToAdmin, postEmailToAdminHR9, postReport, updateReportByLastQuestion, updateReportByQuestionID, uploadAudioToFirebase, uploadSnapShotToFirebase, uploadVideoPerQuestionToFirebase, verifyRoom } from "../services/interview-api.services";
import { Candidate, JobPosition, JobQuestionModel, Question } from "../models/questions.model";
import { isCheckboxQuestion, isMultipleChoiceLike } from "../utils/question-type.util";
import { getPublicInterviewBranding } from "../utils/public-branding.util";
import { useLanguage } from "../providers/language.provider";
import APIService from "../services/api.services";
import RoomInfoComponent from "../components/room-information/room-information.component";
import AudioVisualizer from "../components/audio-wave/audio-wave.component";
import EnglishInterviewLoadingResult from "../components/english-interview-loading-result/english-interview-loading-result";
import MIC from "../assets/images/mic-icon.png";
import MIC_NORMAL from "../assets/images/mic.png";
import STOP_MIC from "../assets/images/stop-mic.png";
import LISTEN from "../assets/images/listen.png";
import NEXT_ICON from "../assets/images/next-icon.png";
import { Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup } from "@mui/material";
import Lottie from "lottie-react";
import NO_MIC_IMG from "../assets/images/mute-microphone.png";
import LOADING_JSON from "../assets/json/loading-blue.json"
import UploadAgainModal from "../modals/upload-again.modal";
import LOGO_SO from "../assets/images/logo-so-white.png";
import LOGO_SO_BLACK from "../assets/images/logo-so-black.png";
import LOGO_SO_WATERMARK from "../assets/images/logo-so-watermark.png"
import LOGO_PROFI_WATERMARK from "../assets/images/water-mark-profi.png"
import LOGO_PROFI from "../assets/images/logo-profi.svg";
import IMG_JOBHACK from "../assets/images/logo-jobhack.png";
import ENGLISH_INTERVIEW_LOGO from "../assets/images/english-interview-logo.png";
import config from "../config";
import { trackEvent } from "../utils/trackGA4.util";
import { GAConfig } from "../utils/ga-config.util";
import UtilityService from "../utils/ga4.util";
import { buildLandingResultUrl, getSafeDemoReturnUrl } from "../utils/demo-return.util";

const DEFAULT_COMPANY_LOGO_URL = "https://ats-cdn.scoutout.net/images/company/23e6012c-acba-4058-b77b-eb20bd4908d7/career-page/49adcba5-161c-4de1-ad38-3f805c4088d5-1642431354";

const needsVideoRecording = (job?: JobPosition) =>
    Boolean(job?.hardware_require?.record_video && job?.hardware_require?.is_camera);

const buildEmotionPayload = (
    job: JobPosition | undefined,
    log: { snapshot?: string; video_url?: string }
) => {
    if (needsVideoRecording(job) && log.video_url) {
        return { video_url: log.video_url, record_video: true };
    }
    if (log.snapshot) {
        return { image_url: log.snapshot };
    }
    return null;
};

const showCameraPreview = (job?: JobPosition) =>
    Boolean(job?.hardware_require?.is_camera || job?.hardware_require?.record_video);

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const InterviewRoomPage = () => {
    const { id, candidate_id, email } = useParams<{ id: string; candidate_id: string; email: string }>();
    const [isSubmitAgain, setIsSubmitAgain] = useState(false);
    const { setRoomCheck }: any = useContext(ProviderContext);
    const [jobPositionModel, setJobPositionModel] = useState<JobPosition>();
    const audioResponseRef = useRef<HTMLAudioElement | null>(null);
    const navigate = useNavigate();
    const analyserRef = useRef<AnalyserNode | null>(null);
    const dataArrayRef = useRef<Uint8Array | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const animationRef = useRef<number | null>(null);
    const { translations, language, switchLanguage } = useLanguage();
    
    // Check if current ID should be locked to English
    const isEnglishLocked = id && config.language.english_locked_ids.includes(id);
    
    // Set language to English for whitelisted IDs and prevent changes
    useEffect(() => {
        if (isEnglishLocked) {
            console.log(`Setting English as locked language for ID: ${id}`);
            switchLanguage("en");
        }
    }, [id, isEnglishLocked, switchLanguage]);
    const [roomModel, setRoomModel] = useState<JobQuestionModel>();
    const [isStartVisual, SetIsStartVisual] = useState(false);
    const [isListenQuestionAgain, setIsListenQuestionAgain] = useState(false);
    const [isLastQuestion, setIsLastQuestion] = useState(false);
    const [latestAudioBuffer, setLatestAudioBuffer] = useState<AudioBuffer | null>(null);
    const [isShowInfo, setIsShowInfo] = useState(false);
    const [isStartInterview, setIsStartInterview] = useState(false);
    const [isSubmittingReport, setIsSubmittingReport] = useState(false);
    const [isChoiceError, setIsChoiceError] = useState(false);
    const [endTimeCaptured, setEndTimeCaptured] = useState<number | null>(null);
    const [isPlayResponse, setIsPlayingResponse] = useState(false);
    const [checklist, setCheckList] = useState<RoomStatus>({
        check_camera: false,
        check_mic: false
    })
    const [isLoadingQuestion, setIsLoadingQuesion] = useState(false);
    const [audioResponse, setAudioResponse] = useState<Blob>();
    const [hasValidAudioForCurrentQuestion, setHasValidAudioForCurrentQuestion] = useState(false);
    const lastRecordedAudioDurationSecRef = useRef<number | null>(null);
    const answerStartSecondRef = useRef<number | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);
    const [isUserTurn, setIsUserTurn] = useState(false);

    const MIN_AUDIO_BYTES = 1000;
    const MIN_VIDEO_BYTES = 5000;
    const MIN_ANSWER_SECONDS = 5;
    const [videoResponse, setVideoResponse] = useState<Blob | undefined>();
    const videoResponseRef = useRef<Blob | undefined>();
    const videoDurationSecRef = useRef<number>(0);
    const videoMimeRef = useRef<string>("video/webm");
    const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
    const [isStartVideo, setIsStartVideo] = useState(false);
    const [startInfo, setIsStartInfo] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [isMicOn, setIsMicOn] = useState(false);
    const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
    const [pendingSubmissionCount, setPendingSubmissionCount] = useState(0);
    const [failedSubmissionCount, setFailedSubmissionCount] = useState(0);
    const videoRef = useRef<any>(null);
    const [nextQuestion, setNextQuestion] = useState(0);
    const [reportModel, setReportModel] = useState<ReportModel>();
    const [candidateInfo, setCandidateInfo] = useState<Candidate>();
    const [countTime, setCountTime] = useState(0);
    const [currentQuestion, setCurrentQuestion] = useState<Log>();
    const [rePostAgain, setRePostAgain] = useState(0);
    const [isConfirmResponse, setIsConfirmResponse] = useState(false);
    // Load voice and TTS speed from localStorage
    const selectedVoice = (() => {
        const saved = localStorage.getItem('interview_voice_selection');
        return saved || 'M1';
    })();
    const ttsSpeed = "X1";
    const audioRef = useRef<any>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const submitReportTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    /** Preloaded TTS for the next question: when index matches nextQuestion we use this instead of calling getTTS again */
    const preloadedTTSRef = useRef<{ index: number; messageAudio: ArrayBuffer; textAudio: ArrayBuffer } | null>(null);
    /** Preloaded TTS for all questions (filled when user clicks "เริ่มสัมภาษณ์" during preparing phase) */
    const preloadedAllTTSRef = useRef<Map<number, { messageAudio: ArrayBuffer; textAudio: ArrayBuffer }>>(new Map());
    const [isPreparingRoom, setIsPreparingRoom] = useState(false);
    const pendingSubmissionsRef = useRef<Map<string, Promise<void>>>(new Map());
    const failedSubmissionPayloadsRef = useRef<Map<string, {
        questionId: string;
        reportId: string;
        questionLog: any;
        audioBlob: Blob;
    }>>(new Map());

    const SUBMIT_REPORT_TIMEOUT_MS = 90000; // 90 seconds

    const clearSubmitReportTimeout = useCallback(() => {
        if (submitReportTimeoutRef.current) {
            clearTimeout(submitReportTimeoutRef.current);
            submitReportTimeoutRef.current = null;
        }
    }, []);

    const startSubmitReportTimeout = useCallback(() => {
        clearSubmitReportTimeout();
        submitReportTimeoutRef.current = setTimeout(() => {
            submitReportTimeoutRef.current = null;
            setIsSubmittingReport(false);
            setIsSubmittingAnswer(false);
            alert(translations?.sending?.timeout_or_error ?? "Submission failed or took too long. Please try again.");
        }, SUBMIT_REPORT_TIMEOUT_MS);
    }, [clearSubmitReportTimeout, translations]);
    const TEST_MODE = process.env.REACT_APP_ENVIRONMENT === "dddd" ? true : false;
    const query = useQuery();
    const redirectPublicDemoIfNeeded = (reportId?: string) => {
        const returnUrl = getSafeDemoReturnUrl(query.get("return_url"));
        if (query.get("app_id") === "PUBLIC_DEMO" && returnUrl && reportId && id && candidate_id) {
            window.location.assign(buildLandingResultUrl(returnUrl, {
                report_id: reportId,
                candidate_id,
                job_question_id: id,
            }));
            return true;
        }
        return false;
    };
    const findSupportType = () => {

        const possibleMimeTypes = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg',
            'audio/mp4',
            'audio/aac'
        ];

        let supportedMimeType = '';
        for (const type of possibleMimeTypes) {
            if (MediaRecorder.isTypeSupported(type)) {
                supportedMimeType = type;
                break;
            }
        }
        return supportedMimeType;

    }
    const [appId, setAppId] = useState<string | null>("SCOUTOUT");
    const [templateId, setTemplateId] = useState<string | null>("");
    const [companyLogoFailed, setCompanyLogoFailed] = useState(false);

    useEffect(() => {
        // If embedded in an iframe, check parent/host via document.referrer
        try {
            const inIframe = window.self !== window.top;
            if (inIframe && document.referrer) {
                const refHost = new URL(document.referrer).host;
                if (refHost === "english-interview-test.web.app" || refHost === "speakout.scoutout.co") {
                    setAppId("ENGLISH_INTERVIEW_AI");
                    return; // Force override and skip query param handling
                }
            }
        } catch (e) {
            // noop - cross-origin access guards
        }

        if (query.get('app_id')) {
            const id = query.get('app_id');
            setAppId(id);

        } else {
            setAppId("SCOUTOUT");
        }

        if (query.get('template_id')) {
            const id = query.get('template_id');
            setTemplateId(id);
        }
    }, [query]);
    useEffect(() => {
        if (id && candidate_id) {
            verifyRoom(id, candidate_id, email).then((res) => {

                if (res.is_success) {

                    if (res["room"]) {
                        const result = res["room"] as JobPosition;
                        setJobPositionModel(result);
                    }
                    console.log(res);
                }
            }).catch((error) => {
                console.log(error);
                const msg = error?.response?.data?.error;
                if (msg === "Interview period has ended" || msg === "Interview period has not started yet") {
                    alert(
                        msg === "Interview period has ended"
                            ? "ลิงก์สัมภาษณ์หมดอายุแล้ว"
                            : "ยังไม่ถึงช่วงเวลาที่เปิดสัมภาษณ์",
                    );
                }
                if (appId == "PROFI") {
                    window.open(config.partner.profi, "_self");
                } else {
                    navigate("/");
                }


            })
        } else {
            if (appId == "PROFI") {
                window.open(config.partner.profi, "_self");
            } else {
                navigate("/" + id);
            }

        }

    }, [id && candidate_id])
    useEffect(() => {
        if (isStartInterview) {
            recordingIntervalRef.current = setInterval(() => {
                setCountTime((prevTime) => prevTime + 1);
            }, 1000);

            return () => {
                if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
            };
        }

    }, [isStartInterview]);
    useEffect(() => {

        if (reportModel && reportModel.transcript.logs.length > 0) {
            setCurrentQuestion(reportModel?.transcript.logs[nextQuestion])
            if (isStartInterview && reportModel.transcript.logs.length > 0) {
                if (nextQuestion >= reportModel.transcript.logs.length - 1) {
                    setIsLastQuestion(true); // ✅ If last question, set to true
                } else {
                    setIsLastQuestion(false); // ✅ Otherwise, set to false
                }

                if (reportModel.transcript.logs[nextQuestion]?.question?.[language]) {
                    generateSpeech(reportModel.transcript.logs[nextQuestion]?.question?.[language] || '');
                }
            }
        }

    }, [nextQuestion]); // ✅ Proper dependency array




    useEffect(() => {
        if (!mediaStream) return;

        if (jobPositionModel && showCameraPreview(jobPositionModel)) {
            setIsStartVideo(true);
        }

        setCheckList((prev) => {
            const nextCamera = jobPositionModel && showCameraPreview(jobPositionModel) ? true : prev.check_camera;
            if (prev.check_mic === true && prev.check_camera === nextCamera) {
                return prev;
            }
            return {
                ...prev,
                check_mic: true,
                check_camera: nextCamera,
            };
        });
    }, [mediaStream, jobPositionModel])



    useEffect(() => {
        try {
            const handleVisibilityChange = () => {
                // Keep preview alive when user only switches browser tabs.
                // On iOS, backgrounding can invalidate tracks, so release hardware there.
                if (document.hidden && isIOS()) {
                    stopHardware();
                }
            };
            const handlePageHide = () => {
                stopHardware();
            };

            document.addEventListener("visibilitychange", handleVisibilityChange);
            window.addEventListener("pagehide", handlePageHide);

            return () => {
                document.removeEventListener("visibilitychange", handleVisibilityChange);
                window.removeEventListener("pagehide", handlePageHide);
            };
        } catch (err) {
            alert(err);
            console.log("Error in useEffect: ", err);
        }

    }, []);

    useEffect(() => {
        const hasLiveTracks = (stream: MediaStream | null) =>
            !!stream && stream.getTracks().some((track) => track.readyState === "live");

        const handleResume = async () => {
            if (document.hidden) return;
            const currentStream = mediaStreamRef.current;
            const shouldRestart = !hasLiveTracks(currentStream);
            if (!shouldRestart) return;

            console.log("🎥 Resuming media stream");
            try {
                await initialHardware();
            } catch (err) {
                console.error("Failed to restart media stream", err);
            }
        };

        document.addEventListener("visibilitychange", handleResume);
        window.addEventListener("pageshow", handleResume); // back from background

        return () => {
            document.removeEventListener("visibilitychange", handleResume);
            window.removeEventListener("pageshow", handleResume);
        };
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            const hasPendingWork =
                pendingSubmissionsRef.current.size > 0 || failedSubmissionPayloadsRef.current.size > 0;
            if (!hasPendingWork) return;
            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);



    const initialHardware = async () => {
        try {
            // Try to get video and audio
            if (jobPositionModel && jobPositionModel.hardware_require.is_camera) {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: "user", aspectRatio: 1 / 1 },
                    audio: true,
                });
                mediaStreamRef.current = stream;
            } else {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: false,
                    audio: true,
                });
                mediaStreamRef.current = stream;
            }

        } catch (err: any) {
            console.warn("Video+Audio request failed:", err);

            // Fallback: Try only audio if video is not available
            if (err.name === "NotFoundError" || err.name === "OverconstrainedError") {
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({
                        audio: true,
                    });
                    mediaStreamRef.current = stream;
                } catch (audioErr) {
                    console.error("Audio-only request also failed:", audioErr);
                    return; // Can't proceed without any stream
                }
            } else {
                console.error("Unexpected getUserMedia error:", err);
                return;
            }
        }

        // Assign stream to video element if available
        if (jobPositionModel && jobPositionModel.hardware_require.is_camera) {
            if (videoRef.current && mediaStreamRef.current.getVideoTracks().length > 0) {
                videoRef.current.srcObject = mediaStreamRef.current;
            }
        }

        // Set the stream for further use
        setMediaStream(mediaStreamRef.current);
    };
    const nextPage = () => {
        if (jobPositionModel && jobPositionModel.hardware_require.is_camera) {
            if (checklist.check_camera && checklist.check_mic) {
                console.log(checklist);
                setRoomCheck(checklist);
                setIsStartInfo(true);
                getRoomDetail(language);
            }
        } else {
            if (checklist.check_mic) {
                console.log(checklist);
                setRoomCheck(checklist);
                setIsStartInfo(true);
                getRoomDetail(language);
            }
        }

    }

    const setIsVideoReady = useCallback((isReady: boolean) => {
        setCheckList((prev) => {
            if (prev.check_camera === isReady) return prev;
            return { ...prev, check_camera: isReady };
        });
    }, []);
    // 🔥 Convert seconds to HH:MM:SS format
    const formatTime = (seconds: number): string => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return [h, m, s].map(v => String(v).padStart(2, "0")).join(":"); // "HH:mm:ss"
    };

    const getAudioDurationFromBlob = async (blob: Blob): Promise<number | null> => {
        try {
            const arrayBuffer = await blob.arrayBuffer();
            const audioContext = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
            await audioContext.close();
            if (Number.isFinite(audioBuffer.duration) && audioBuffer.duration > 0) {
                return audioBuffer.duration;
            }
        } catch {
            // ignore and try metadata fallback below
        }

        try {
            const duration = await new Promise<number>((resolve, reject) => {
                const url = URL.createObjectURL(blob);
                const audio = new Audio();
                audio.preload = "metadata";
                audio.onloadedmetadata = () => {
                    const d = audio.duration;
                    URL.revokeObjectURL(url);
                    if (Number.isFinite(d) && d > 0) resolve(d);
                    else reject(new Error("invalid audio duration"));
                };
                audio.onerror = () => {
                    URL.revokeObjectURL(url);
                    reject(new Error("audio metadata read failed"));
                };
                audio.src = url;
            });
            return duration;
        } catch {
            return null;
        }
    };
    const startResponse = async () => {
        if (reportModel && reportModel.transcript && reportModel.transcript.logs[nextQuestion]) {
            setIsMicOn(true);
            SetIsStartVisual(true);
            setIsConfirmResponse(false);
            answerStartSecondRef.current = countTime;
            lastRecordedAudioDurationSecRef.current = null;
            const reportMod = { ...reportModel };
            reportMod.transcript.logs[nextQuestion].duration_start = formatTime(countTime);
            if (videoRef.current && jobPositionModel && jobPositionModel.hardware_require.is_camera) {
                const snapshot = await videoRef.current.takeSnapshot();
                reportMod.transcript.logs[nextQuestion].snapshot_blob = snapshot;
            }
            if (videoRef.current?.startRecording && needsVideoRecording(jobPositionModel)) {
                videoRef.current.startRecording();
            }

            setReportModel(reportMod);
        }
    }
    const onSubmitInterview = async (reportMod: ReportModel) => {
        try {

            await Promise.all(
                reportMod.transcript.logs.map(async (log, index) => {
                    if (log.audio_blob) {
                        const audioUrl = await uploadAudioToFirebase(
                            log.audio_blob,
                            log.question_id,
                            candidate_id,
                            findSupportType()
                        );
                        reportMod.transcript.logs[index] = {
                            ...log,
                            audio_url: audioUrl,
                            audio_blob: null,
                        };
                    }
                })
            );

            // 3. Upload snapshot blobs
            await Promise.all(
                reportMod.transcript.logs.map(async (log, index) => {
                    if (log.snapshot_blob) {
                        const imageUrl = await uploadSnapShotToFirebase(
                            log.snapshot_blob,
                            `${log.question_id}_${candidate_id}`
                        );
                        reportMod.transcript.logs[index] = {
                            ...log,
                            snapshot: imageUrl,
                            snapshot_blob: null,
                        };
                    }
                })
            );

            // 4. Analyze emotion
            await Promise.all(
                reportMod.transcript.logs.map(async (log, index) => {
                    const emotionPayload = buildEmotionPayload(jobPositionModel, log);
                    if (emotionPayload) {
                        const emotional = await getEmotional(emotionPayload);
                        reportMod.transcript.logs[index].emotion = emotional.data.response;
                    }
                })
            );

            // 5. Save updated report
            setReportModel(reportMod);

            const response = await postReport(id, reportMod.candidate_detail.candidate_id, reportMod);

            if (response.is_success) {
                setReportModel(reportMod);
                stopHardware();
                trackEvent(GAConfig.action.completeInterview, UtilityService());
                if(response["report_short_id"]) {
                    popOutIframe(response["report_short_id"], reportMod.report_id);
                } else {
                    navigate(`/${id}/${candidate_id}/${email}/result?app_id=${appId}`, { state: { report: reportMod } });
                }
            } else {
                throw new Error("Failed to post report.");
            }
        } catch (error: any) {
            // handleInterviewError(error);
        }
    };

    const stopHardware = async () => {
        if (videoRef.current?.stopCamera) {
            videoRef.current.stopCamera();
        } else if (videoRef.current?.getTracks) {
            videoRef.current.getTracks().forEach((track: MediaStreamTrack) => track.stop());
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((track) => track.stop());
            mediaStreamRef.current = null;
        }
        if (audioRef.current) {
            audioRef.current.stopRecording();
            audioRef.current.stopMicTrack();

        }
        audioRef.current = null;
        setMediaStream(null);
        setIsStartVideo(false);
        mediaStreamRef.current = null; // optional: clear ref
    };

    // const handleInterviewError = (error: any) => {
    //     console.error("❌ Interview submission error:", error);

    //     if (rePostAgain < 3 && reportModel) {
    //         setRePostAgain(rePostAgain + 1);
    //         onSubmitInterview(reportModel);
    //     } else {
    //         setIsSubmittingReport(false);
    //         setIsSubmitAgain(true);
    //         alert("Submission failed. Please try again later.\n" + error.message || error);
    //     }
    // };

    /** Build TTS message and text for a given question index (for preload or play) */
    const buildMessageForIndex = useCallback((index: number, questionText: string, totalLogs: number) => {
        if (!candidateInfo) return { message: "", text: questionText };
        if (language === "en") {
            if (index > 0 && index < totalLogs - 1) return { message: "Thank you for your answer. Next question is " + questionText, text: questionText };
            if (index > 0 && index === totalLogs - 1) return { message: "Thank you for your answer. The Last question is " + questionText, text: questionText };
            return { message: "Nice to meet you " + candidateInfo.firstname + ". We will start with the first question " + questionText, text: questionText };
        }
        if (index > 0 && index < totalLogs - 1) return { message: "ขอบคุณสำหรับคำตอบ คำถามถัดไป " + questionText, text: questionText };
        if (index > 0 && index === totalLogs - 1) return { message: "ขอบคุณสำหรับคำตอบ และนี่คือคำถามสุดท้าย " + questionText, text: questionText };
        return { message: "ยินดีที่ได้พบกัน คุณ" + candidateInfo.firstname + ".เรามาเริ่มกันที่คำถามแรก " + questionText, text: questionText };
    }, [candidateInfo, language]);

    /** Preload TTS for question at index (runs in background, stores in preloadedTTSRef) */
    const preloadTTSForQuestionIndex = useCallback(async (index: number) => {
        if (!reportModel?.transcript?.logs?.[index] || TEST_MODE) return;
        const questionText = reportModel.transcript.logs[index]?.question?.[language] || "";
        if (!questionText) return;
        const totalLogs = reportModel.transcript.logs.length;
        const { message, text } = buildMessageForIndex(index, questionText, totalLogs);
        if (!message) return;
        try {
            const [fileAudio, fileAudioSupport] = await Promise.all([
                APIService().getTTS({ input: message, voice: selectedVoice, speed: ttsSpeed }),
                APIService().getTTS({ input: text, voice: selectedVoice, speed: ttsSpeed })
            ]);
            if (fileAudio?.data && fileAudioSupport?.data) {
                preloadedTTSRef.current = { index, messageAudio: fileAudio.data, textAudio: fileAudioSupport.data };
            }
        } catch (e) {
            // ignore preload errors
        }
    }, [reportModel, language, selectedVoice, buildMessageForIndex]);

    /** Preload TTS for all questions; when index 0 is ready, hide modal and start interview; rest load in background */
    const preloadAllTTS = useCallback(async () => {
        const logs = reportModel?.transcript?.logs || [];
        const firstQuestionText = logs[0]?.question?.[language] || roomModel?.questions?.[0]?.question?.[language] || "";
        const startInterviewNow = () => {
            setIsPreparingRoom(false);
            setIsStartInterview(true);
            if (firstQuestionText) generateSpeech(firstQuestionText);
        };

        if (!logs.length) {
            startInterviewNow();
            return;
        }
        if (TEST_MODE || !candidateInfo) {
            startInterviewNow();
            return;
        }

        const total = logs.length;
        let hasStarted = false;
        preloadedAllTTSRef.current = new Map();

        for (let i = 0; i < total; i++) {
            try {
                const questionText = logs[i]?.question?.[language] || "";
                if (!questionText) {
                    if (i === 0 && !hasStarted) {
                        hasStarted = true;
                        startInterviewNow();
                    }
                    continue;
                }
                const { message, text } = buildMessageForIndex(i, questionText, total);
                if (!message) {
                    if (i === 0 && !hasStarted) {
                        hasStarted = true;
                        startInterviewNow();
                    }
                    continue;
                }

                const [fileAudio, fileAudioSupport] = await Promise.all([
                    APIService().getTTS({ input: message, voice: selectedVoice, speed: ttsSpeed }),
                    APIService().getTTS({ input: text, voice: selectedVoice, speed: ttsSpeed })
                ]);
                if (fileAudio?.data && fileAudioSupport?.data) {
                    preloadedAllTTSRef.current.set(i, { messageAudio: fileAudio.data, textAudio: fileAudioSupport.data });
                }

                if (i === 0 && !hasStarted) {
                    hasStarted = true;
                    startInterviewNow();
                }
            } catch (e) {
                if (i === 0 && !hasStarted) {
                    hasStarted = true;
                    startInterviewNow();
                }
            }
        }

        if (!hasStarted) {
            startInterviewNow();
        }
    }, [reportModel, roomModel, candidateInfo, language, selectedVoice, buildMessageForIndex]);

    const generateSpeech = async (text) => {
        let message = "";

        if (reportModel && candidateInfo) {
            if (language == "en") {
                if (nextQuestion > 0) {
                    message = "Thank you for your answer. Next question is " + text;
                } else if (nextQuestion == reportModel?.transcript.logs.length - 1) {
                    message = "Thank you for your answer. The Last question is " + text;
                } else {
                    message = "Nice to meet you " + candidateInfo.firstname + ". We will start with the first question " + text;
                }
            } else {
                if (nextQuestion > 0) {
                    message = "ขอบคุณสำหรับคำตอบ คำถามถัดไป " + text;
                } else if (nextQuestion == reportModel?.transcript.logs.length - 1) {
                    message = "ขอบคุณสำหรับคำตอบ และนี่คือคำถามสุดท้าย " + text;
                } else {
                    message = "ยินดีที่ได้พบกัน คุณ" + candidateInfo.firstname + ".เรามาเริ่มกันที่คำถามแรก " + text;
                }
            }
        }
        setIsUserTurn(false);
        if (TEST_MODE) {
            if (reportModel && reportModel.transcript && reportModel.transcript.logs[nextQuestion]) {
                const reportMod = { ...reportModel };
                reportMod.transcript.logs[nextQuestion].response.index = 99;
                if (isCheckboxQuestion(reportMod.transcript.logs[nextQuestion])) {
                    reportMod.transcript.logs[nextQuestion].response.indexes = [];
                    reportMod.transcript.logs[nextQuestion].response.value = "";
                }
                reportMod.transcript.logs[nextQuestion].question_read_end = formatTime(countTime);
                console.log(reportMod);
                setReportModel(reportMod);
                setIsMicOn(false);
                SetIsStartVisual(false);
                setIsConfirmResponse(false);
                setIsLoadingQuesion(false);
                setIsUserTurn(true);
            }
        } else {
            try {
                setIsLoadingQuesion(true);
                let messageArrayBuffer: ArrayBuffer;
                let textArrayBuffer: ArrayBuffer;
                const allPreloaded = preloadedAllTTSRef.current.get(nextQuestion);
                const singlePreloaded = preloadedTTSRef.current?.index === nextQuestion ? preloadedTTSRef.current : null;
                if (allPreloaded) {
                    messageArrayBuffer = allPreloaded.messageAudio;
                    textArrayBuffer = allPreloaded.textAudio;
                } else if (singlePreloaded) {
                    preloadedTTSRef.current = null;
                    messageArrayBuffer = singlePreloaded.messageAudio;
                    textArrayBuffer = singlePreloaded.textAudio;
                } else {
                    const [fileAudio, fileAudioSupport] = await Promise.all([
                        APIService().getTTS({ input: message, voice: selectedVoice, speed: ttsSpeed }),
                        APIService().getTTS({ input: text, voice: selectedVoice, speed: ttsSpeed })
                    ]);
                    messageArrayBuffer = fileAudio?.data;
                    textArrayBuffer = fileAudioSupport?.data;
                }
                if (!allPreloaded && reportModel?.transcript?.logs?.[nextQuestion + 1]) {
                    preloadTTSForQuestionIndex(nextQuestion + 1);
                }

                if (textArrayBuffer) {
                    const audioContext = new AudioContext();
                    const audioBuffer = await audioContext.decodeAudioData(textArrayBuffer);
                    setLatestAudioBuffer(audioBuffer);
                }

                if (messageArrayBuffer) {
                    const arrayBuffer = messageArrayBuffer;
                    const blob = new Blob([new Uint8Array(arrayBuffer)], { type: "audio/mpeg" });
                    const url = URL.createObjectURL(blob);

                    const audioUsers = new Audio(url);
                    audioResponseRef.current = audioUsers;
                    const audioContext = new AudioContext();
                    const analyser = audioContext.createAnalyser();
                    const source = audioContext.createBufferSource();

                    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
                    source.buffer = audioBuffer;
                    source.connect(analyser);
                    analyser.connect(audioContext.destination);

                    analyser.fftSize = 256;
                    const bufferLength = analyser.frequencyBinCount;
                    const dataArray = new Uint8Array(bufferLength);

                    audioContextRef.current = audioContext;
                    analyserRef.current = analyser;
                    dataArrayRef.current = dataArray;

                    drawBars();
                    source.start();
                    source.onended = () => {
                        setIsUserTurn(true);
                        if (reportModel && reportModel.transcript && reportModel.transcript.logs[nextQuestion]) {
                            const reportMod = { ...reportModel };
                            reportMod.transcript.logs[nextQuestion].response.index = 99;
                            if (isCheckboxQuestion(reportMod.transcript.logs[nextQuestion])) {
                                reportMod.transcript.logs[nextQuestion].response.indexes = [];
                                reportMod.transcript.logs[nextQuestion].response.value = "";
                            }
                            reportMod.transcript.logs[nextQuestion].question_read_end = formatTime(countTime);
                            console.log(reportMod);
                            setReportModel(reportMod);
                            setIsMicOn(false);
                            SetIsStartVisual(false);
                            setIsConfirmResponse(false);
                        }
                    };

                    setIsLoadingQuesion(false);
                }
            } catch (error) {
                console.error("Error generating speech:", error);
                throw error; // Propagate error to caller
            }
        }
    };


    const convertQuestionsToLogs = (questions: Question[]): Log[] => {
        return questions.map((q) => {
            const isCheckbox = isCheckboxQuestion(q);
            return {
                question_id: q.question_id, // Generate unique ID
                question: q.question, // Assign Question model directly
                question_type: q.question_type || "voice",
                answer_type: q.answer_type || "",
                ui_variant: q.ui_variant || "",
                choices: q.choices || [],
                topic: q.topic ? q.topic : "",
                need_skills: q.need_skills || [""],
                question_read_end: "",
                response: {
                    value: "",
                    // 99 is the "no selection yet" sentinel used by submit validation;
                    // checkbox questions use indexes[] instead and keep index=99.
                    index: isCheckbox ? 99 : 0,
                    indexes: isCheckbox ? [] : undefined,
                },
            } as Log;
        });
    };
    const getRoomDetail = (languageParam) => {
        // Use locked language if ID is in whitelist, otherwise use the parameter
        const effectiveLanguage = isEnglishLocked ? "en" : languageParam;

        verifyRoom(id, candidate_id, email).then((res) => {

            if (res.is_success) {
                console.log(res);
                const candidate = res["candidate"] as Candidate;
                setCandidateInfo(candidate);

                getRoom(id, email, candidate_id).then((res) => {
                    if (res.is_success) {
                        const roomResult = res.message as JobQuestionModel;
                        const result = appId === "PUBLIC_DEMO"
                            ? { ...roomResult, questions: roomResult.questions.slice(0, 3) }
                            : roomResult;
                        console.log(result);
                        setRoomModel(result);
                        const transcriptLogInit: Log[] = convertQuestionsToLogs(result.questions);
                        if (appId == "INTERVIEW_AI") {
                            console.log(appId);
                            if (templateId) {
                                getHardSkill(templateId).then((res) => {
                                    const transcriptLogInitCustom: Log[] = convertQuestionsToLogs(res.data.questions);
                                    transcriptLogInit.push(...transcriptLogInitCustom);
                                    const reportInit: ReportModel = {
                                        job_question_id: result.job_question_id,
                                        candidate_detail: candidate,
                                        report_id: candidate.report_id || "",
                                        job_position: result.job_position,
                                        company_id: result.company_id,
                                        language: effectiveLanguage,
                                        room_status: checklist,
                                        app_id: appId,
                                        transcript: {
                                            total_duration: 0,
                                            logs: transcriptLogInit,
                                            video_url: ""
                                        }
                                    }
                                    setCurrentQuestion(reportInit.transcript.logs[nextQuestion])

                                    if (candidate.report_id) {
                                        setIsShowInfo(true);
                                        console.log(reportInit);
                                        setReportModel(reportInit);
                                    } else {
                                        postReport(id, reportInit.candidate_detail.candidate_id, reportInit).then((result) => {
                                            if (result.is_success) {
                                                reportInit.report_id = result["report_id"];
                                                if(result["report_short_id"]) {
                                                    reportInit.report_short_id = result["report_short_id"];
                                                }

                                                console.log(reportInit);
                                                setReportModel(reportInit);
                                                setIsShowInfo(true);
                                            }
                                        })
                                    }
                                })
                            }
                        } else {
                            const reportInit: ReportModel = {
                                job_question_id: result.job_question_id,
                                candidate_detail: candidate,
                                report_id: candidate.report_id || "",
                                job_position: result.job_position,
                                company_id: result.company_id,
                                app_id: appId || "ScoutOut",
                                language: effectiveLanguage,
                                room_status: checklist,
                                transcript: {
                                    total_duration: 0,
                                    logs: transcriptLogInit,
                                    video_url: ""
                                }
                            }
                            setCurrentQuestion(reportInit.transcript.logs[nextQuestion])

                            if (candidate.report_id) {
                                setIsShowInfo(true);
                                console.log(reportInit);
                                setReportModel(reportInit);
                            } else {
                                postReport(id, reportInit.candidate_detail.candidate_id, reportInit).then((result) => {
                                    if (result.is_success) {
                                        reportInit.report_id = result["report_id"];
                                        if(result["report_short_id"]) {
                                            reportInit.report_short_id = result["report_short_id"];
                                        }

                                        console.log(reportInit);
                                        setReportModel(reportInit);
                                        setIsShowInfo(true);
                                    }
                                })
                            }
                        }



                    }
                });
            }
        })

    }

    const runWithRetry = async <T,>(fn: () => Promise<T>, retries = 1): Promise<T> => {
        let lastErr: any;
        for (let i = 0; i <= retries; i++) {
            try {
                return await fn();
            } catch (e) {
                lastErr = e;
                if (i < retries) {
                    await new Promise((r) => setTimeout(r, 300 * (i + 1)));
                }
            }
        }
        throw lastErr;
    };

    type PendingQuestionPayload = {
        questionId: string;
        reportId: string;
        questionLog: any;
        audioBlob: Blob;
        videoBlob?: Blob;
        videoDurationSec?: number;
        videoMime?: string;
    };

    const submitQuestionInBackground = async (payload: PendingQuestionPayload) => {
        const { questionId, reportId, questionLog, audioBlob, videoBlob, videoDurationSec, videoMime } = payload;
        const uploadPromises: Promise<void>[] = [];
        let audioUrl: string | null = null;
        let videoUrl: string | null = null;

        uploadPromises.push(
            runWithRetry(
                () => uploadAudioToFirebase(audioBlob, questionId, candidate_id, findSupportType()),
                1
            ).then((url) => {
                audioUrl = url as string;
            })
        );

        if (videoBlob && videoBlob.size >= MIN_VIDEO_BYTES) {
            uploadPromises.push(
                runWithRetry(
                    () =>
                        uploadVideoPerQuestionToFirebase(
                            videoBlob,
                            questionId,
                            candidate_id!,
                            videoMime || videoBlob.type || "video/webm"
                        ),
                    1
                ).then((url) => {
                    videoUrl = url as string;
                })
            );
        }

        await Promise.all(uploadPromises);

        const logForUpdate: any = {
            ...questionLog,
            audio_url: audioUrl,
            snapshot_blob: null,
        };
        if (videoUrl) {
            logForUpdate.video_url = videoUrl;
            logForUpdate.video_duration_sec = videoDurationSec ?? 0;
            logForUpdate.video_mime = videoMime || videoBlob?.type;
        }

        if (questionLog.snapshot_blob) {
            try {
                const imageUrl = await runWithRetry(
                    () => uploadSnapShotToFirebase(
                        questionLog.snapshot_blob || new Blob(),
                        `${questionId}_${candidate_id}`
                    ),
                    1
                );
                logForUpdate.snapshot = imageUrl;
            } catch {
                logForUpdate.snapshot = "";
            }
        }

        const emotionPayload =
            needsVideoRecording(jobPositionModel) && videoUrl
                ? { video_url: videoUrl, record_video: true }
                : logForUpdate.snapshot
                    ? { image_url: logForUpdate.snapshot }
                    : null;

        if (emotionPayload) {
            logForUpdate.emotion = "กำลังวิเคราะห์";
        } else if (logForUpdate.snapshot === "") {
            logForUpdate.emotion = "ไม่สามารถวิเคราะห์อารมณ์ได้";
        }

        await runWithRetry(
            () => updateReportByQuestionID(id, candidate_id, reportId, questionId, logForUpdate),
            1
        );

        if (emotionPayload) {
            getEmotional(emotionPayload)
                .then((emotional) => {
                    const logWithEmotion = { ...logForUpdate, emotion: emotional.data.response };
                    updateReportByQuestionID(id, candidate_id, reportId, questionId, logWithEmotion).catch(() => { });
                })
                .catch(() => {
                    const logWithFallback = { ...logForUpdate, emotion: "ไม่สามารถวิเคราะห์อารมณ์ได้" };
                    updateReportByQuestionID(id, candidate_id, reportId, questionId, logWithFallback).catch(() => { });
                });
        }
    };

    const registerPendingSubmission = (questionId: string, payload: PendingQuestionPayload) => {
        if (pendingSubmissionsRef.current.has(questionId)) {
            return;
        }
        const task = submitQuestionInBackground(payload);
        pendingSubmissionsRef.current.set(questionId, task);
        setPendingSubmissionCount(pendingSubmissionsRef.current.size);
        task.then(() => {
            failedSubmissionPayloadsRef.current.delete(questionId);
            setFailedSubmissionCount(failedSubmissionPayloadsRef.current.size);
        }).catch((error) => {
            console.error("[Submit] background submission failed:", questionId, error);
            failedSubmissionPayloadsRef.current.set(questionId, payload);
            setFailedSubmissionCount(failedSubmissionPayloadsRef.current.size);
        }).finally(() => {
            pendingSubmissionsRef.current.delete(questionId);
            setPendingSubmissionCount(pendingSubmissionsRef.current.size);
        });
    };

    const flushPendingSubmissions = async (): Promise<boolean> => {
        const pendingTasks = Array.from(pendingSubmissionsRef.current.values());
        if (pendingTasks.length > 0) {
            await Promise.allSettled(pendingTasks);
        }
        if (failedSubmissionPayloadsRef.current.size === 0) return true;

        for (const [questionId, payload] of Array.from(failedSubmissionPayloadsRef.current.entries())) {
            try {
                await submitQuestionInBackground(payload);
                failedSubmissionPayloadsRef.current.delete(questionId);
            } catch (error) {
                console.error("[Submit] retry failed:", questionId, error);
            }
        }
        setFailedSubmissionCount(failedSubmissionPayloadsRef.current.size);
        return failedSubmissionPayloadsRef.current.size === 0;
    };

    const submitAnswer = () => {
        if (isSubmittingAnswer || isSubmittingReport) {
            return;
        }
        if (isLastQuestion && reportModel) {
            console.log("[Submit] 🟢 isSubmittingReport=true, starting flow");
            setIsSubmittingReport(true);
            // onSubmitInterview(reportModel)
            if (isMultipleChoiceLike(currentQuestion?.question_type)) {
                submitAnswerByChoice()
            } else {
                submitAnswerByQuestionId();
            }

        } else {
            if (isMultipleChoiceLike(currentQuestion?.question_type)) {
                submitAnswerByChoice()
            } else {
                submitAnswerByQuestionId();
            }
        }

    }

    useEffect(() => {
        if (endTimeCaptured !== null && reportModel) {
            const reportMod = { ...reportModel };
            reportMod.transcript.logs[nextQuestion].duration_end = formatTime(endTimeCaptured);
            setReportModel(reportMod);
        }
    }, [endTimeCaptured])

    const captureVideoOnStop = async () => {
        if (!needsVideoRecording(jobPositionModel) || !videoRef.current?.stopRecordingAndReturnBlob) {
            return;
        }
        const blob = await videoRef.current.stopRecordingAndReturnBlob();
        if (blob && blob.size >= MIN_VIDEO_BYTES) {
            const timerDurationSec =
                answerStartSecondRef.current !== null
                    ? Math.max(0, countTime - answerStartSecondRef.current)
                    : 0;
            videoDurationSecRef.current = Math.max(timerDurationSec, 1);
            videoMimeRef.current = blob.type || "video/webm";
            videoResponseRef.current = blob;
            setVideoResponse(blob);
        } else {
            videoResponseRef.current = undefined;
            videoDurationSecRef.current = 0;
            setVideoResponse(undefined);
        }
    };

    const toggleMic = async () => {
        if (isMicOn) {
            setEndTimeCaptured(countTime);
            setIsConfirmResponse(true);
            SetIsStartVisual(false);
            if (audioRef.current) {
                console.log("audioRef");
                await audioRef.current.stopRecording();
            }
            await captureVideoOnStop();
        }

    }
    const submitAnswerByChoice = async () => {

        const logForCheck = reportModel?.transcript?.logs?.[nextQuestion];
        const isCheckbox = isCheckboxQuestion(logForCheck);
        const hasValidChoice = isCheckbox
            ? Array.isArray(logForCheck?.response?.indexes) && logForCheck!.response.indexes!.length > 0
            : (logForCheck?.response?.index !== 99);
        if (reportModel && reportModel.transcript && reportModel.transcript.logs[nextQuestion] && hasValidChoice) {
            setIsSubmittingAnswer(true);
            const reportMod = { ...reportModel };
            if (isLastQuestion) {
                const allSaved = await flushPendingSubmissions();
                if (!allSaved) {
                    alert("ยังมีบางคำตอบบันทึกไม่สำเร็จ กรุณารอสักครู่แล้วกดส่งอีกครั้ง");
                    setIsSubmittingAnswer(false);
                    setIsSubmittingReport(false);
                    return;
                }
                startSubmitReportTimeout();
                reportMod.transcript.total_duration = countTime;
                setReportModel(reportMod);
                updateReportByLastQuestion(id,
                    candidate_id,
                    reportMod.report_id,
                    reportMod.transcript.logs[nextQuestion].question_id, reportMod.transcript.logs[nextQuestion], countTime, appId).then((res) => {
                        if (res.is_success) {
                            clearSubmitReportTimeout();
                            setReportModel(reportModel);
                            stopHardware();
                            trackEvent(GAConfig.action.completeInterview, UtilityService());
                            const inIframe = window.self !== window.top;
                            if (reportMod.report_short_id && inIframe) {
                                popOutIframe(reportMod.report_short_id, reportMod.report_id);
                            } else if (!redirectPublicDemoIfNeeded(reportMod.report_id)) {
                                navigate(`/${id}/${candidate_id}/${email}/${reportMod.report_id}/result?app_id=${appId}`, { state: { report: reportMod } });
                            }
                        } else {
                            clearSubmitReportTimeout();
                            setIsSubmittingReport(false);
                            setIsSubmittingAnswer(false);
                        }
                    }).catch((error) => {
                        console.log(error);
                        clearSubmitReportTimeout();
                        setIsSubmittingReport(false);
                        setIsSubmittingAnswer(false);
                    });

            } else {
                reportMod.transcript.total_duration = countTime;
                setReportModel(reportMod);
                updateReportByQuestionID(id,
                    candidate_id,
                    reportMod.report_id,
                    reportMod.transcript.logs[nextQuestion].question_id, reportMod.transcript.logs[nextQuestion]).then((res) => {
                    }).catch((error) => {
                        console.log(error);
                    });

                setIsMicOn(false);
                SetIsStartVisual(false);
                setIsConfirmResponse(false);
                setIsSubmittingAnswer(false);
                setNextQuestion(nextQuestion + 1);
            }
        } else {
            setIsChoiceError(true);
            setIsSubmittingAnswer(false);
            setIsSubmittingReport(false);
        }
    };
    const submitAnswerByQuestionId = async () => {
        const t0 = performance.now();
        const logStep = (label: string) => console.log(`[Submit] ${label} +${(performance.now() - t0).toFixed(0)}ms`);
        try {
            if (!reportModel?.transcript?.logs?.[nextQuestion]) return;
            const isVoiceQuestion = !isMultipleChoiceLike(currentQuestion?.question_type);
            if (isVoiceQuestion && (!audioResponse || audioResponse.size < MIN_AUDIO_BYTES)) {
                alert(translations?.interview?.please_record_before_send ?? "Please record your answer before submitting.");
                return;
            }
            const videoBlobForSubmit = videoResponseRef.current ?? videoResponse;
            if (
                isVoiceQuestion &&
                needsVideoRecording(jobPositionModel) &&
                (!videoBlobForSubmit || videoBlobForSubmit.size < MIN_VIDEO_BYTES)
            ) {
                alert(
                    translations?.interview?.please_record_video_before_send ??
                        "Please record your video answer before submitting."
                );
                return;
            }
            if (isVoiceQuestion) {
                const timerDurationSec = answerStartSecondRef.current !== null ? Math.max(0, countTime - answerStartSecondRef.current) : 0;
                const decodedDurationSec = lastRecordedAudioDurationSecRef.current ?? 0;
                const effectiveDurationSec = Math.max(timerDurationSec, decodedDurationSec);
                if (effectiveDurationSec < MIN_ANSWER_SECONDS) {
                    alert(`กรุณาตอบอย่างน้อย ${MIN_ANSWER_SECONDS} วินาทีก่อนส่งคำตอบ`);
                    return;
                }
            }
            logStep("1. start submitAnswerByQuestionId");
            setIsSubmittingAnswer(true);
            const reportMod = { ...reportModel };

            // Optimistic flow for non-last question:
            // move user to next question immediately and submit current answer in background.
            if (!isLastQuestion) {
                reportMod.transcript.total_duration = countTime;
                setReportModel(reportMod);

                const currentIndex = nextQuestion;
                const questionLogSnapshot = {
                    ...reportMod.transcript.logs[currentIndex],
                    duration_end: formatTime(endTimeCaptured ?? countTime),
                };
                const questionId = questionLogSnapshot.question_id;
                const audioBlob = audioResponse as Blob;
                registerPendingSubmission(questionId, {
                    questionId,
                    reportId: reportMod.report_id,
                    questionLog: questionLogSnapshot,
                    audioBlob,
                    videoBlob: videoBlobForSubmit,
                    videoDurationSec: videoDurationSecRef.current,
                    videoMime: videoMimeRef.current,
                });

                setIsMicOn(false);
                SetIsStartVisual(false);
                setIsConfirmResponse(false);
                setIsSubmittingAnswer(false);
                setAudioResponse(undefined);
                videoResponseRef.current = undefined;
                setVideoResponse(undefined);
                videoDurationSecRef.current = 0;
                setHasValidAudioForCurrentQuestion(false);
                lastRecordedAudioDurationSecRef.current = null;
                answerStartSecondRef.current = null;
                setNextQuestion(currentIndex + 1);
                return;
            }

            // Last question keeps strong consistency flow; flush any pending background submissions first.
            const allSaved = await flushPendingSubmissions();
            if (!allSaved) {
                alert("ยังมีบางคำตอบบันทึกไม่สำเร็จ กรุณารอสักครู่แล้วกดส่งอีกครั้ง");
                setIsSubmittingAnswer(false);
                setIsSubmittingReport(false);
                return;
            }

            startSubmitReportTimeout();
            setUploadProgress(0);
            logStep("2. before uploadAudioToFirebase");
            const questionIdForUpload = reportModel.transcript.logs[nextQuestion].question_id;
            const lastVideoBlob = videoBlobForSubmit;
            const audioUploadPromise = uploadAudioToFirebase(
                audioResponse,
                questionIdForUpload,
                candidate_id,
                findSupportType(),
                (percent) => setUploadProgress(percent)
            );
            const videoUploadPromise =
                lastVideoBlob && lastVideoBlob.size >= MIN_VIDEO_BYTES
                    ? uploadVideoPerQuestionToFirebase(
                          lastVideoBlob,
                          questionIdForUpload,
                          candidate_id!,
                          videoMimeRef.current || lastVideoBlob.type || "video/webm",
                          (percent) => setUploadProgress(percent)
                      )
                    : Promise.resolve(null);

            const [audioUrl, videoUrl] = await Promise.all([audioUploadPromise, videoUploadPromise]);
            logStep("3. after uploadAudioToFirebase");
            setUploadProgress(null);
            reportMod.transcript.logs[nextQuestion].audio_url = audioUrl;
            if (videoUrl) {
                reportMod.transcript.logs[nextQuestion].video_url = videoUrl;
                reportMod.transcript.logs[nextQuestion].video_duration_sec = videoDurationSecRef.current;
                reportMod.transcript.logs[nextQuestion].video_mime = videoMimeRef.current;
            }
            videoResponseRef.current = undefined;
            setVideoResponse(undefined);
            videoDurationSecRef.current = 0;
            if (reportModel.transcript.logs[nextQuestion].snapshot_blob) {
                try {
                    logStep("4. before uploadSnapShotToFirebase");
                    const imageUrl = await uploadSnapShotToFirebase(
                        reportModel.transcript.logs[nextQuestion].snapshot_blob || new Blob(),
                        `${reportModel.transcript.logs[nextQuestion].question_id}_${candidate_id}`
                    );
                    reportModel.transcript.logs[nextQuestion].snapshot = imageUrl;
                    reportModel.transcript.logs[nextQuestion].snapshot_blob = null;
                    reportMod.transcript.logs[nextQuestion].snapshot = imageUrl;
                    reportMod.transcript.logs[nextQuestion].snapshot_blob = null;
                } catch (error) {
                    reportModel.transcript.logs[nextQuestion].snapshot = "";
                    reportModel.transcript.logs[nextQuestion].snapshot_blob = null;
                    reportMod.transcript.logs[nextQuestion].snapshot = "";
                    reportMod.transcript.logs[nextQuestion].snapshot_blob = null;
                }
            }

            {
                const questionId = reportMod.transcript.logs[nextQuestion].question_id;
                const reportId = reportMod.report_id;
                const logForReport = { ...reportMod.transcript.logs[nextQuestion] };
                const emotionPayload = buildEmotionPayload(jobPositionModel, logForReport);
                if (emotionPayload) {
                    logStep("5. getEmotional will run in background");
                    reportMod.transcript.logs[nextQuestion].emotion = "กำลังวิเคราะห์";
                    getEmotional(emotionPayload)
                        .then((emotional) => {
                            const logWithEmotion = { ...logForReport, emotion: emotional.data.response };
                            updateReportByQuestionID(id, candidate_id, reportId, questionId, logWithEmotion).catch(() => {});
                        })
                        .catch(() => {
                            const logWithFallback = { ...logForReport, emotion: "ไม่สามารถวิเคราะห์อารมณ์ได้" };
                            updateReportByQuestionID(id, candidate_id, reportId, questionId, logWithFallback).catch(() => {});
                        });
                } else if (!logForReport.snapshot && !logForReport.video_url) {
                    reportMod.transcript.logs[nextQuestion].emotion = "ไม่สามารถวิเคราะห์อารมณ์ได้";
                }
            }


            if (isLastQuestion) {
                reportMod.transcript.total_duration = countTime;
                setReportModel(reportMod);
                stopHardware();
                logStep("7. before updateReportByLastQuestion");
                updateReportByLastQuestion(id,
                    candidate_id,
                    reportMod.report_id,
                    reportMod.transcript.logs[nextQuestion].question_id,                     reportMod.transcript.logs[nextQuestion], countTime, appId).then((res) => {
                        logStep("8. after updateReportByLastQuestion, res.is_success=" + !!res?.is_success);
                        if (res.is_success) {
                            clearSubmitReportTimeout();
                            setReportModel(reportModel);
                            // For HR9, use new dedicated endpoint that only requires candidate_id
                            if (appId === "PUBLIC_DEMO") {
                                trackEvent(GAConfig.action.completeInterview, UtilityService());
                                if (!redirectPublicDemoIfNeeded(reportMod.report_id)) {
                                    navigate(`/${id}/${candidate_id}/${email}/${reportMod.report_id}/result?app_id=${appId}`, { state: { report: reportMod } });
                                }
                            } else if (appId === "HR9") {
                                logStep("9a. before postEmailToAdminHR9");
                                const emailDataHR9 = {
                                    candidate_id: reportMod.candidate_detail.candidate_id
                                };
                                console.log("📧 [Email] HR9 payload:", emailDataHR9);
                                console.log("📧 [Email] HR9 reportMod.job_position (for debug):", {
                                    company_name: reportMod.job_position?.company_name ?? "undefined",
                                    company_logo: reportMod.job_position?.company_logo ? "(set)" : "undefined/empty"
                                });
                                
                                postEmailToAdminHR9(emailDataHR9).then((res) => {
                                    logStep("10a. after postEmailToAdminHR9, before navigate");
                                    trackEvent(GAConfig.action.completeInterview, UtilityService());
                                    const inIframe = window.self !== window.top;
                                    if (reportMod.report_short_id && inIframe) {
                                        popOutIframe(reportMod.report_short_id, reportMod.report_id);
                                    } else {
                                        navigate(`/${id}/${candidate_id}/${email}/${reportMod.report_id}/result?app_id=${appId}`, { state: { report: reportMod } });
                                    }
                                }).catch((error) => {
                                    logStep("10a. postEmailToAdminHR9 catch, navigating anyway");
                                    const status = error?.response?.status;
                                    if (status === 400) console.log("📧 [Email] HR9 returned 400, still navigating to Result");
                                    trackEvent(GAConfig.action.completeInterview, UtilityService());
                                    const inIframe = window.self !== window.top;
                                    if (reportMod.report_short_id && inIframe) {
                                        popOutIframe(reportMod.report_short_id, reportMod.report_id);
                                    } else {
                                        navigate(`/${id}/${candidate_id}/${email}/${reportMod.report_id}/result?app_id=${appId}`, { state: { report: reportMod } });
                                    }
                                });
                            } else {
                                logStep("9b. before postEmailToAdmin (non-HR9)");
                                const rawCompanyName = reportMod.job_position.company_name;
                                const rawCompanyLogo = reportMod.job_position.company_logo;
                                const companyName = rawCompanyName || "Company";
                                const imgCover = rawCompanyLogo || DEFAULT_COMPANY_LOGO_URL;
                                const emailData: any = {
                                    name: reportMod.candidate_detail.firstname,
                                    job: reportMod.job_position.job_title,
                                    link: reportMod.company_id + "/job/" + id + "/" + reportMod.candidate_detail.candidate_id,
                                    companyName,
                                    imgCover
                                };
                                const missing: string[] = [];
                                if (!rawCompanyName) missing.push("company_name");
                                if (!rawCompanyLogo) missing.push("company_logo (using default URL)");
                                if (missing.length) console.log("📧 [Email] ค่าที่ขาด/ใช้ fallback:", missing);
                                console.log("📧 [Email] Payload ที่ส่งไป complete_interview:", { ...emailData, imgCover: imgCover ? imgCover.substring(0, 60) + "..." : imgCover });
                                postEmailToAdmin(emailData).then((res) => {
                                    logStep("10b. after postEmailToAdmin, before navigate");
                                    trackEvent(GAConfig.action.completeInterview, UtilityService());
                                    const inIframe = window.self !== window.top;
                                    if (reportMod.report_short_id && inIframe) {
                                        console.log("📧 [Navigate] report_short_id มีค่า + อยู่ใน iframe → popOutIframe");
                                        popOutIframe(reportMod.report_short_id, reportMod.report_id);
                                    } else {
                                        if (reportMod.report_short_id && !inIframe) {
                                            console.log("📧 [Navigate] report_short_id มีค่าแต่ไม่ได้อยู่ใน iframe → navigate ไป Result");
                                        }
                                        navigate(`/${id}/${candidate_id}/${email}/${reportMod.report_id}/result?app_id=${appId}`, { state: { report: reportMod } });
                                    }
                                }).catch((error) => {
                                    logStep("10b. postEmailToAdmin catch, navigating anyway");
                                    const status = error?.response?.status;
                                    if (status === 400) console.log("📧 [Email] complete_interview returned 400, still navigating to Result");
                                    trackEvent(GAConfig.action.completeInterview, UtilityService());
                                    const inIframe = window.self !== window.top;
                                    if (reportMod.report_short_id && inIframe) {
                                        popOutIframe(reportMod.report_short_id, reportMod.report_id);
                                    } else {
                                        navigate(`/${id}/${candidate_id}/${email}/${reportMod.report_id}/result?app_id=${appId}`, { state: { report: reportMod } });
                                    }
                                });
                            }

                        } else {
                            logStep("8b. updateReportByLastQuestion res.is_success=false, stopping");
                            setUploadProgress(null);
                            clearSubmitReportTimeout();
                            setIsSubmittingReport(false);
                            setIsSubmittingAnswer(false);
                        }

                    }).catch((error) => {
                        logStep("8c. updateReportByLastQuestion catch: " + (error?.message || error));
                        setUploadProgress(null);
                        clearSubmitReportTimeout();
                        setIsSubmittingReport(false);
                        setIsSubmittingAnswer(false);
                    });

            }

        } catch (error) {
            console.error("[Submit] ❌ submitAnswerByQuestionId catch:", error);
            setUploadProgress(null);
            clearSubmitReportTimeout();
            setIsSubmittingReport(false);
            setIsSubmittingAnswer(false);
        }
    };

    const popOutIframe = (report_code: string, reportId?: string) => {
        const payload: { url?: string; reportId?: string } =
          appId === "PROFI"
            ? reportId
              ? { reportId }
              : { url: config.partner.profi }
            : appId === "ENGLISH_INTERVIEW_AI"
              ? { url: config.partner.english_interview_ai + "?report_code=" + report_code }
              : {};
        window.parent.postMessage({ type: "REDIRECT", payload }, "*");
    }

    //Submit answer
    const onAudioStop = async (audioBlob: Blob) => {
        if (!reportModel?.transcript?.logs?.[nextQuestion]) return;
        lastRecordedAudioDurationSecRef.current = null;

        if (!audioBlob || audioBlob.size < MIN_AUDIO_BYTES) {
            setAudioResponse(undefined);
            setHasValidAudioForCurrentQuestion(false);
            // alert(translations?.interview?.audio_invalid_or_too_short ?? "Recording is invalid or too short. Please record again.");
            return;
        }

        try {
            const durationSec = await getAudioDurationFromBlob(audioBlob);
            lastRecordedAudioDurationSecRef.current = durationSec;
            const timerDurationSec = answerStartSecondRef.current !== null ? Math.max(0, countTime - answerStartSecondRef.current) : 0;
            const effectiveDurationSec = Math.max(timerDurationSec, durationSec || 0);
            if (effectiveDurationSec < MIN_ANSWER_SECONDS) {
                setAudioResponse(undefined);
                setHasValidAudioForCurrentQuestion(false);
                alert(`กรุณาตอบอย่างน้อย ${MIN_ANSWER_SECONDS} วินาที`);
                return;
            }
        } catch (e) {
            setAudioResponse(undefined);
            setHasValidAudioForCurrentQuestion(false);
            alert("ไฟล์เสียงมีปัญหา กรุณาอัดใหม่อีกครั้ง");
            return;
        }

        setAudioResponse(audioBlob);
        setHasValidAudioForCurrentQuestion(true);
    };


    //start record, timer, generate speech — show "preparing room" and preload all TTS then start
    const clickStartInterview = () => {
        if (roomModel && reportModel && roomModel.questions && reportModel.transcript?.logs?.length) {
            setIsShowInfo(false);
            setIsPreparingRoom(true);
            preloadAllTTS();
        }
    }

    const replaySpeech = async () => {
        if (!latestAudioBuffer) return;
        if (isListenQuestionAgain) return;

        try {
            setIsListenQuestionAgain(true);
            const audioContext = new AudioContext();
            const analyser = audioContext.createAnalyser();
            const source = audioContext.createBufferSource();

            source.buffer = latestAudioBuffer; // ✅ use decoded AudioBuffer
            source.connect(analyser);
            analyser.connect(audioContext.destination);

            analyser.fftSize = 256;
            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);

            audioContextRef.current = audioContext;
            analyserRef.current = analyser;
            dataArrayRef.current = dataArray;

            drawBars();
            source.start();

            source.onended = () => {
                setIsListenQuestionAgain(false);
            };

        } catch (err) {
            console.error("Error replaying speech:", err);
        }
    };
    const stopAudio = () => {
        try {
            setIsPlayingResponse(false);

            // ✅ Stop and reset the <audio> element
            if (audioResponseRef.current) {
                audioResponseRef.current.pause();
                audioResponseRef.current.currentTime = 0;
            }
            // ✅ Close and cleanup audio context
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close();
            }

            // ✅ Clean up refs
            audioResponseRef.current = null;
            audioContextRef.current = null;
            analyserRef.current = null;
            dataArrayRef.current = null;

        } catch (error) {
            console.error("Error stopping audio:", error);
        }
    };


    const playAudio = async () => {
        if (audioResponse) {
            try {
                setIsPlayingResponse(true);
                const url = URL.createObjectURL(audioResponse);
                const audioUsers = new Audio(url);
                audioResponseRef.current = audioUsers;
                const audioContext = new AudioContext();
                const analyser = audioContext.createAnalyser();
                const source = audioContext.createBufferSource();
                // Read the audio file into a buffer
                const arrayBuffer = await audioResponse.arrayBuffer();
                const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
                source.buffer = audioBuffer;
                source.connect(analyser);
                analyser.connect(audioContext.destination);

                analyser.fftSize = 256;
                const bufferLength = analyser.frequencyBinCount;
                const dataArray = new Uint8Array(bufferLength);

                // Set refs
                audioContextRef.current = audioContext;
                analyserRef.current = analyser;
                dataArrayRef.current = dataArray;

                // Start visualization and playback
                drawBars();
                source.start();
                source.onended = () => {
                    // alert("end");
                    setIsPlayingResponse(false);
                };
            } catch (error) {
                alert(error);
                setIsPlayingResponse(false);
            }

        }

    }


    const pauseAudio = () => {
        stopAudio();
    }

    const handleAnswerChoice = (choice) => {
        if (reportModel) {
            const reportMod = { ...reportModel };
            reportMod.transcript.logs[nextQuestion].response.index = choice;
            if (currentQuestion?.choices && currentQuestion.choices.length > 0) {
                if (language == "en") {
                    reportMod.transcript.logs[nextQuestion].response.value = currentQuestion.choices[choice].en;
                } else {
                    reportMod.transcript.logs[nextQuestion].response.value = currentQuestion.choices[choice].th;
                }

            }
            console.log(reportMod);
            setIsChoiceError(false);
            setReportModel(reportMod);
        }
    }

    /** Toggle a single checkbox option for multi-select questions */
    const handleAnswerChoiceCheckbox = (choiceIndex: number) => {
        if (!reportModel) return;
        const reportMod = { ...reportModel };
        const log = reportMod.transcript.logs[nextQuestion];
        const prev = Array.isArray(log.response.indexes) ? [...log.response.indexes] : [];
        const at = prev.indexOf(choiceIndex);
        if (at >= 0) {
            prev.splice(at, 1);
        } else {
            prev.push(choiceIndex);
        }
        prev.sort((a, b) => a - b);
        log.response.indexes = prev;
        // Keep `index` field in a useful state: store the first selected (or 99 if empty)
        // so legacy code that checks `index !== 99` still treats this question as "answered".
        log.response.index = prev.length > 0 ? prev[0] : 99;
        // Persist a human-readable concatenated value for downstream display/aggregation.
        const choices = currentQuestion?.choices || [];
        log.response.value = prev
            .map((i) => {
                const ch = choices[i];
                if (!ch) return "";
                return language === "en" ? ch.en : ch.th;
            })
            .filter(Boolean)
            .join(" | ");
        if (prev.length > 0) setIsChoiceError(false);
        setReportModel(reportMod);
    };

    const drawRoundedRect = (
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        radius: number
    ) => {
        const r = Math.min(radius, height / 2);
        ctx.beginPath();
        ctx.moveTo(x, y + r);
        ctx.arcTo(x, y + height, x + width, y + height, r);
        ctx.arcTo(x + width, y + height, x + width, y, r);
        ctx.arcTo(x + width, y, x, y, r);
        ctx.arcTo(x, y, x, y + height, r);
        ctx.closePath();
        ctx.fill();
    };
    const drawBars = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const canvasCtx = canvas.getContext("2d");
        if (!canvasCtx) return;

        const analyser = analyserRef.current;
        if (analyser) {
            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength); // ✅ Correct usage with default ArrayBuffer
            dataArrayRef.current = dataArray;

            if (!analyser || !dataArray) return;

            const draw = () => {
                animationRef.current = requestAnimationFrame(draw);

                analyser.getByteFrequencyData(dataArray);

                // เคลียร์ Canvas ก่อนวาดใหม่
                canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

                const width = canvas.width;
                const height = canvas.height;
                const centerX = width / 2;
                const centerY = height / 2;
                const barWidth = 6; // ความกว้างของแท่ง Bar
                const gap = 4; // ระยะห่างระหว่างแท่ง Bar
                const numBars = Math.floor(width / (barWidth + gap)) / 2; // แบ่งครึ่ง ซ้าย-ขวา

                // วาดเส้นฐาน
                canvasCtx.strokeStyle = "rgba(74, 37, 225, 0.15)";
                canvasCtx.lineWidth = 2;
                canvasCtx.beginPath();
                canvasCtx.moveTo(0, centerY);
                canvasCtx.lineTo(width, centerY);
                canvasCtx.stroke();

                // วาดแท่ง Bar ซ้าย-ขวา ออกจากกึ่งกลาง
                for (let i = 0; i < numBars; i++) {
                    const amplitude = dataArray[i] / 255; // Normalize 0 - 1
                    const barHeight = amplitude * (height * 0.4); // กำหนดความสูงของแท่ง Bar

                    // คำนวณตำแหน่งแท่ง Bar (ซ้าย-ขวา)
                    const xRight = centerX + i * (barWidth + gap);
                    const xLeft = centerX - (i * (barWidth + gap) + barWidth);
                    const yTop = centerY - barHeight;
                    const yBottom = centerY + barHeight;

                    // ใช้ Gradient Color
                    const gradient = canvasCtx.createLinearGradient(0, yTop, 0, yBottom);
                    gradient.addColorStop(0, "#4A25E1"); // สีบน
                    gradient.addColorStop(1, "#FF7FD8"); // สีล่าง


                    canvasCtx.fillStyle = gradient;

                    drawRoundedRect(canvasCtx, xRight, yTop, barWidth, barHeight * 2, 5);
                    drawRoundedRect(canvasCtx, xLeft, yTop, barWidth, barHeight * 2, 5);
                }
            };

            draw();
        }

    };

    const onSubmitAgain = () => {
        if (reportModel) {
            console.log("[Submit] 🟢 onSubmitAgain: isSubmittingReport=true, starting flow");
            setIsSubmitAgain(false);
            setIsSubmittingReport(true);
            submitAnswerByQuestionId();
            // onSubmitInterview(reportModel)
        }
    }

    // Preparation phase = before interview starts (checklist + room-info screen)
    const isPreparationPhase = !isStartInterview;

    const handleBack = () => {
        navigate(appId === "PUBLIC_DEMO" ? "/demo" : "/" + id + "/" + candidate_id + "/verify/");
    };

    return (
        <div
            className="interview-control"
            style={{ background: "linear-gradient(170deg, #ffffff 0%, #f4f0ff 100%)" }}
        >
            {/* Header — back button only during preparation; always black logo */}
            <div className="ir-prep-header">
                {isPreparationPhase ? (
                    <button className="ir-back-btn" onClick={handleBack} aria-label="back">
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                ) : (
                    <div className="ir-header-spacer" />
                )}
                {appId === "SCOUTOUT"
                    ? <img src={LOGO_SO_BLACK} className="ir-prep-logo" alt="logo" />
                    : appId === "PROFI"
                        ? <img src={LOGO_PROFI} className="ir-prep-logo ir-prep-logo-profi" alt="logo" />
                        : appId === "ENGLISH_INTERVIEW_AI"
                            ? <img src={ENGLISH_INTERVIEW_LOGO} className="ir-prep-logo" alt="logo" />
                            : appId === "INTERVIEW_AI" || appId === "PUBLIC_DEMO"
                                ? <img src={IMG_JOBHACK} className="ir-prep-logo" alt="logo" />
                                : appId === "PUBLIC_INTERVIEW_AI"
                                    ? (() => {
                                        // Source priority for the public interview logo:
                                        //   1. sessionStorage stash from create-public.page (matches what the
                                        //      candidate first saw on the form page)
                                        //   2. roomModel/jobPositionModel from getRoom API (company_logo / logoImageUrl)
                                        //   3. IMG_JOBHACK fallback (also used on onError)
                                        const stash = getPublicInterviewBranding(
                                            roomModel?.company_id,
                                            id,
                                        );
                                        const apiLogo = String(
                                            jobPositionModel?.company_logo ||
                                            (jobPositionModel as any)?.logoImageUrl ||
                                            ""
                                        ).trim();
                                        const publicLogoUrl = String(stash?.logoUrl || apiLogo || "").trim();
                                        const showCompanyLogo = Boolean(publicLogoUrl) && !companyLogoFailed;
                                        const altCompany =
                                            stash?.companyName ||
                                            jobPositionModel?.company_name ||
                                            "Company";
                                        // #region agent log
                                        try {
                                          const allKeys: string[] = [];
                                          for (let i=0;i<sessionStorage.length;i++){const k=sessionStorage.key(i);if(k&&k.startsWith('public_interview_branding'))allKeys.push(k);}
                                          fetch('http://127.0.0.1:7306/ingest/c0ca0ffc-101a-41d9-9a0f-54f5fb097d04',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'04060b'},body:JSON.stringify({sessionId:'04060b',hypothesisId:'A,B,C,D,E',location:'interview-room.page.tsx:prep-logo-render',message:'PUBLIC_INTERVIEW_AI logo resolution',data:{appId,id_param:id,roomModel_company_id:roomModel?.company_id,jobPositionModel_company_logo:jobPositionModel?.company_logo,jobPositionModel_logoImageUrl:(jobPositionModel as any)?.logoImageUrl,jobPositionModel_company_name:jobPositionModel?.company_name,companyLogoFailed,stash_present:!!stash,stash_logoUrl:stash?.logoUrl,stash_companyName:stash?.companyName,resolved_publicLogoUrl:publicLogoUrl,showCompanyLogo,finalSrc:showCompanyLogo?publicLogoUrl:'IMG_JOBHACK',sessionStorageBrandingKeys:allKeys,storageReadAttemptKey:roomModel?.company_id?`public_interview_branding:${roomModel?.company_id}:${id}`:'(no_company_id_yet)',href:typeof window!=='undefined'?window.location.href:'',isIframe:typeof window!=='undefined'?(window.self!==window.top):'unknown'},timestamp:Date.now()})}).catch(()=>{});
                                        } catch (e) {
                                          fetch('http://127.0.0.1:7306/ingest/c0ca0ffc-101a-41d9-9a0f-54f5fb097d04',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'04060b'},body:JSON.stringify({sessionId:'04060b',hypothesisId:'E',location:'interview-room.page.tsx:prep-logo-render',message:'sessionStorage access threw',data:{err:String((e as Error).message)},timestamp:Date.now()})}).catch(()=>{});
                                        }
                                        // #endregion
                                        return (
                                            <img
                                                src={showCompanyLogo ? publicLogoUrl : IMG_JOBHACK}
                                                className="ir-prep-logo"
                                                alt={`${altCompany} logo`}
                                                onError={() => setCompanyLogoFailed(true)}
                                            />
                                        );
                                      })()
                                    : <div />}
                <div className="ir-header-spacer" />
            </div>

            {isPreparingRoom && (
                <div className="submitting-control" style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(255,255,255,0.98)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div className="submitting-loading-control">
                        <Lottie animationData={LOADING_JSON} loop className="submitting-loading"></Lottie>
                        <span className="text-bold">{translations?.room?.preparing_room ?? "กำลังเตรียมห้องสัมภาษณ์"}</span>
                        <span className="text-normal">{translations?.interview?.loading_question ?? "กำลังโหลดคำถาม"}</span>
                    </div>
                </div>
            )}
            {
                isSubmittingReport &&
                (appId === "ENGLISH_INTERVIEW_AI" ? (
                    <EnglishInterviewLoadingResult />
                ) : (
                    <div className="submitting-control">
                        <div className="submitting-loading-control">
                            <Lottie animationData={LOADING_JSON} loop className="submitting-loading"></Lottie>
                            <span className="text-bold">{translations.sending.title}</span>
                            <span className="text-normal">{translations.sending.desc_1} <br></br>
                                {translations.sending.desc_2}<br></br>
                                {translations.sending.desc_3}</span>
                            {uploadProgress !== null && (
                                <div className="submitting-upload-progress">
                                    <span className="text-normal">{translations.sending?.upload_progress ?? "Uploading audio"}… {Math.round(uploadProgress)}%</span>
                                    <div className="submitting-upload-progress-bar">
                                        <div className="submitting-upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))
            }{
                !isSubmittingReport &&
                <div className="prepare-room">

                    {
                        id && candidate_id && !isSubmittingReport &&
                        <div className="prepare-room-main-control">
                            {
                                isStartInterview && roomModel && candidateInfo && reportModel ?
                                    <div className="prepare-top-control">
                                        <span className="prepare-title">{translations.interview.question} {nextQuestion + 1} / {reportModel.transcript.logs.length}</span>
                                        <span className="conver-room-duration">{formatTime(countTime)}</span>
                                    </div>
                                    : <div className="prepare-top-control"><span className="prepare-title">{translations.room.prepare}</span></div>
                            }
                            {/* {
                                jobPositionModel && !jobPositionModel.hardware_require.is_camera &&
                                <div className="prepare-not-allow-control">
                                        <div className="not-allow-content-control">
                                            <img src={NO_CAMERA_IMG} className="camera-img"></img>
                                            <span className="text-normal">{translations.room.check_title}</span>
                                            <span className="text-bold">{translations.room.check_desc}</span>
                                        </div>
                                        {
                                            appId == "SCOUTOUT" ?
                                                <img src={LOGO_SO_WATERMARK} className="watermark"></img> :
                                                appId == "PROFI" ?
                                                    <img src={LOGO_PROFI_WATERMARK} className="watermark-custom"></img> :
                                                    <></>
                                        }
                                    </div>

                            } */}

                            {
                                (mediaStream && videoRef && isStartVideo && jobPositionModel && showCameraPreview(jobPositionModel)) ?
                                    <div className="video-comp-control">
                                        <VideoConversation
                                            appId={appId}
                                            mediaStream={mediaStream}
                                            ref={videoRef}
                                            setIsVideoReady={setIsVideoReady}
                                        />
                                    </div> :
                                    !reportModel &&
                                    <div className="prepare-not-allow-control">
                                        <div className="not-allow-content-control">
                                            {
                                                jobPositionModel && jobPositionModel.hardware_require.is_camera
                                                    ? <img src={NO_CAMERA_IMG} className="camera-img"></img> :
                                                    <img src={NO_MIC_IMG} className="camera-img"></img>
                                            }

                                            <span className="text-normal">
                                                {
                                                    jobPositionModel && jobPositionModel.hardware_require.is_camera ?
                                                        translations.room.check_title : translations.room.check_title_mic_only
                                                }</span>
                                            <span className="text-bold">{translations.room.check_desc}</span>
                                        </div>
                                        {
                                            appId == "SCOUTOUT" ?
                                                <img src={LOGO_SO_WATERMARK} className="watermark"></img> :
                                                appId == "PROFI" ?
                                                    <img src={LOGO_PROFI_WATERMARK} className="watermark-custom"></img> :
                                                    <></>
                                        }
                                    </div>
                            }

                            {
                                startInfo ?
                                    <>
                                        {
                                            isShowInfo && roomModel && candidateInfo && reportModel ?
                                                <>
                                                    <RoomInfoComponent job_position={roomModel.job_position}
                                                        onStart={clickStartInterview}
                                                        report_model={reportModel}  ></RoomInfoComponent>
                                                </> :

                                                isStartInterview && roomModel && candidateInfo && reportModel &&
                                                <>
                                                    <div className={jobPositionModel && jobPositionModel.hardware_require.is_camera ? "questions-control" : "questions-control questions-control-mic-only"}>
                                                        {
                                                            !isLoadingQuestion &&
                                                            reportModel.transcript.logs[nextQuestion]?.question?.[language] &&
                                                            <span className="questions-text">"{reportModel.transcript.logs[nextQuestion].question[language]}"</span>
                                                        }

                                                        {
                                                            isLoadingQuestion &&
                                                            <div className="circle-progress-control">
                                                                <Lottie animationData={LOADING_JSON} loop className="circle-lottie"></Lottie>
                                                                <span className="progress-text">{translations.interview.loading_question}</span>
                                                            </div>
                                                        }

                                                        {
                                                            isSubmittingAnswer &&
                                                            <div className="circle-progress-control">
                                                                <Lottie animationData={LOADING_JSON} loop className="circle-lottie"></Lottie>
                                                                <span className="progress-text">{translations.interview.sending_answer}</span>
                                                            </div>
                                                        }
                                                        {
                                                            false && pendingSubmissionCount > 0 &&
                                                            <div className="circle-progress-control">
                                                                <span className="progress-text">
                                                                    กำลังบันทึกคำตอบก่อนหน้า... ({pendingSubmissionCount})
                                                                    {failedSubmissionCount > 0 ? ` | ล้มเหลว ${failedSubmissionCount}` : ""}
                                                                </span>
                                                            </div>
                                                        }
                                                        {
                                                            isUserTurn && currentQuestion && isMultipleChoiceLike(currentQuestion.question_type) ?

                                                                <>
                                                                    <div className="question-choice-main">
                                                                        {
                                                                            currentQuestion.choices && currentQuestion.choices.length > 0 && (() => {
                                                                                const currentLog = reportModel.transcript.logs[nextQuestion];
                                                                                const isCheckbox = isCheckboxQuestion(currentLog ?? currentQuestion);
                                                                                if (isCheckbox) {
                                                                                    const selectedIndexes: number[] = Array.isArray(currentLog?.response?.indexes)
                                                                                        ? currentLog!.response.indexes!
                                                                                        : [];
                                                                                    return (
                                                                                        <FormGroup
                                                                                            className="question-choice-control"
                                                                                        >
                                                                                            {currentQuestion.choices!.map((choice, index) => {
                                                                                                const checked = selectedIndexes.includes(index);
                                                                                                return (
                                                                                                    <div className="question-choice-item" key={index}>
                                                                                                        <FormControlLabel
                                                                                                            control={
                                                                                                                <Checkbox
                                                                                                                    checked={checked}
                                                                                                                    onChange={() => handleAnswerChoiceCheckbox(index)}
                                                                                                                    sx={{
                                                                                                                        borderRadius: "6px",
                                                                                                                        "& .MuiSvgIcon-root": {
                                                                                                                            borderRadius: "6px",
                                                                                                                            backgroundColor: "transparent",
                                                                                                                            fill: !isChoiceError && checked ? "#4A25E1" :
                                                                                                                                isChoiceError ? "red" :
                                                                                                                                    "#D8CCFF"
                                                                                                                        },
                                                                                                                    }}
                                                                                                                />
                                                                                                            }
                                                                                                            label={language == "en" ? choice.en : choice.th}
                                                                                                        />
                                                                                                    </div>
                                                                                                );
                                                                                            })}
                                                                                        </FormGroup>
                                                                                    );
                                                                                }
                                                                                const selectedIdx = currentLog?.response?.index;
                                                                                // อย่าใช้ || "" — เลข 0 (ข้อก) เป็น falsy ทำให้ RadioGroup คิดว่าไม่ได้เลือก แล้วสไตล์ active ไม่ตรงกับข้อจริง
                                                                                const radioGroupValue =
                                                                                    selectedIdx === undefined || selectedIdx === null || selectedIdx === 99
                                                                                        ? ""
                                                                                        : selectedIdx;
                                                                                return (
                                                                                <RadioGroup
                                                                                    name="answer_choices"
                                                                                    className="question-choice-control"
                                                                                    value={radioGroupValue}
                                                                                    onChange={(e) => handleAnswerChoice(Number(e.target.value))}
                                                                                >
                                                                                    {currentQuestion.choices.map((choice, index) => (
                                                                                        <div className="question-choice-item" key={index}>
                                                                                            <FormControlLabel
                                                                                                value={index}
                                                                                                control={<Radio
                                                                                                    sx={{
                                                                                                        borderRadius: "6px",
                                                                                                        "& .MuiSvgIcon-root": {
                                                                                                            borderRadius: "6px",
                                                                                                            backgroundColor: "transparent",
                                                                                                            fill: !isChoiceError && selectedIdx === index ? "#4A25E1" :
                                                                                                                isChoiceError ? "red" :
                                                                                                                    "#D8CCFF"
                                                                                                        },
                                                                                                    }}
                                                                                                />}
                                                                                                label={language == "en" ? choice.en : choice.th}
                                                                                            />
                                                                                        </div>
                                                                                    ))}
                                                                                </RadioGroup>
                                                                                );
                                                                            })()
                                                                        }
                                                                        <Button className="button-std question-choice-button-submit" onClick={submitAnswer}>ส่งคำตอบ</Button>
                                                                    </div>

                                                                </> :


                                                                <>


                                                                    {
                                                                        isMicOn && mediaStream && isStartVisual &&
                                                                        <div className={jobPositionModel && jobPositionModel.hardware_require.is_camera ? "question-audio-wave-control" : "question-audio-wave-control question-audio-wave-control-mic-only"}>
                                                                            <AudioVisualizer ref={audioRef} isStart={isStartVisual} stream={mediaStream} onStop={onAudioStop}></AudioVisualizer>
                                                                        </div>
                                                                    }
                                                                    {
                                                                        !isUserTurn &&
                                                                        <div className={jobPositionModel && jobPositionModel.hardware_require.is_camera ? "question-audio-wave-control" : "question-audio-wave-control question-audio-wave-control-mic-only"}>
                                                                            <div className="audio-wave-control">
                                                                                <canvas ref={canvasRef} className="audio-wave-canvas" ></canvas>
                                                                            </div>
                                                                        </div>
                                                                    }
                                                                    {/* {
                                                            isPlayResponse &&
                                                            <div className="question-audio-wave-control">
                                                                <div className="audio-wave-control">
                                                                    <canvas ref={canvasRef} className="audio-wave-canvas" ></canvas>
                                                                </div>
                                                            </div>
                                                        } */}
                                                                    {
                                                                        isMicOn && isConfirmResponse && !isSubmittingAnswer &&
                                                                        <div className={jobPositionModel && jobPositionModel.hardware_require.is_camera ? "question-confrim-control " : "question-confrim-control question-confrim-control-mic-mode"}>
                                                                            <div className="question-action-item-control">
                                                                                {
                                                                                    isPlayResponse ?
                                                                                        <>
                                                                                            <div className="ripple-wrapper" onClick={pauseAudio}>
                                                                                                <span className="ripple-circle"></span>
                                                                                                <span className="ripple-circle"></span>
                                                                                                <span className="ripple-circle"></span>
                                                                                                <img src={STOP_MIC} className="question-mic-img" ></img>
                                                                                            </div>
                                                                                            <span className="question-action-text">{translations.interview.stop_answer}</span>
                                                                                        </> :
                                                                                        <>
                                                                                            <div className="ripple-wrapper" onClick={playAudio}>
                                                                                                <img src={LISTEN} className="question-mic-img" ></img>
                                                                                            </div>
                                                                                            <span className="question-action-text">{translations.interview.listen_answer}</span>
                                                                                        </>
                                                                                }
                                                                            </div>
                                                                            <div className="question-action-item-control" style={isPlayResponse ? { opacity: "0.2" } : {}}>

                                                                                <div className="ripple-wrapper" onClick={isPlayResponse ? () => { } : startResponse}>
                                                                                    <img src={MIC_NORMAL} className="question-mic-img" ></img>
                                                                                </div>
                                                                                <span className="question-action-text">{translations.interview.answer_again}</span>
                                                                            </div>
                                                                            <div className="question-action-item-control" style={isPlayResponse || (!isMultipleChoiceLike(currentQuestion?.question_type) && !hasValidAudioForCurrentQuestion) ? { opacity: "0.2" } : {}}>
                                                                                <div
                                                                                    className="ripple-wrapper"
                                                                                    onClick={isPlayResponse ? () => { } : (isMultipleChoiceLike(currentQuestion?.question_type) || hasValidAudioForCurrentQuestion) ? submitAnswer : () => alert(translations?.interview?.please_record_before_send ?? "Please record your answer before submitting.")}
                                                                                >
                                                                                    <img src={NEXT_ICON} className="question-mic-img" alt="" />
                                                                                </div>
                                                                                <span className="question-action-text">{translations.interview.send_answer}</span>
                                                                            </div>

                                                                        </div>
                                                                    }

                                                                    {
                                                                        isMicOn && !isConfirmResponse ?
                                                                            <div className="question-action-item-control" onClick={toggleMic}>

                                                                                <img src={STOP_MIC} className="question-mic-img" ></img>
                                                                                <span className="question-action-text" style={{ marginTop: "16px" }}>{translations.interview.stop_answer}</span>
                                                                            </div>
                                                                            : !isConfirmResponse && isUserTurn &&
                                                                            <div className={jobPositionModel && jobPositionModel.hardware_require.is_camera ? "question-confrim-control" : "question-confrim-control question-confrim-control-mic-mode"}>
                                                                                <div className="question-action-item-control">
                                                                                    <div className="ripple-wrapper" onClick={replaySpeech}>
                                                                                        {
                                                                                            isListenQuestionAgain &&
                                                                                            <>
                                                                                                <span className="ripple-circle"></span>
                                                                                                <span className="ripple-circle"></span>
                                                                                                <span className="ripple-circle"></span>
                                                                                            </>
                                                                                        }
                                                                                        <img src={LISTEN} className="question-mic-img" ></img>
                                                                                    </div>
                                                                                    <span className="question-action-text">{translations.interview.listen_again}</span>
                                                                                </div>
                                                                                <div className="question-action-item-control" style={isListenQuestionAgain ? { opacity: "0.2" } : {}}>
                                                                                    <div className="ripple-wrapper" onClick={isListenQuestionAgain ? () => { } : startResponse}>
                                                                                        {
                                                                                            !isListenQuestionAgain &&
                                                                                            <>
                                                                                                <span className="ripple-circle"></span>
                                                                                                <span className="ripple-circle"></span>
                                                                                                <span className="ripple-circle"></span>
                                                                                            </>
                                                                                        }

                                                                                        <img src={MIC} className="question-mic-img" ></img>
                                                                                    </div>
                                                                                    <span className="question-action-text" style={{ marginTop: "16px" }}>{translations.interview.press_answer}</span>
                                                                                </div>
                                                                            </div>
                                                                    }
                                                                </>
                                                        }

                                                    </div>

                                                </>
                                        }

                                    </> :
                                    jobPositionModel &&
                                    <>

                                        <div className="prepare-checklist">
                                            <span className="prepare-checklist-title">{translations.room.check}</span>
                                            <div className="prepare-checklist-grid-control">
                                                {
                                                    jobPositionModel.hardware_require.is_camera &&
                                                    <div className="border-primary-std prepare-checklist-item-control" onClick={initialHardware}>
                                                        <img src={checklist.check_camera ? CHECK_PURPLE : FAILED_PURPLE} className="icon"></img>
                                                        <span className="text">{translations.room.camera}</span>
                                                    </div>
                                                }
                                                {
                                                    jobPositionModel.hardware_require.is_mic &&
                                                    <div className="border-primary-std prepare-checklist-item-control" onClick={initialHardware}>
                                                        <img src={checklist.check_mic ? CHECK_PURPLE : FAILED_PURPLE} className="icon"></img>
                                                        <span className="text">{translations.room.mic}</span>
                                                    </div>
                                                }

                                            </div>


                                            <Button className="button-std prepare-checklist-button" onClick={checklist.check_mic ? nextPage : initialHardware}>
                                                {checklist.check_mic ? translations.room.button_start : translations.room.button_allow}</Button>
                                        </div>
                                    </>

                            }



                        </div>
                    }


                </div>
            }

            {
                isSubmitAgain && <UploadAgainModal submit={() => onSubmitAgain()} isOpen={isSubmitAgain} onClose={() => setIsSubmitAgain(false)}></UploadAgainModal>
            }

        </div >
    )
}

export default InterviewRoomPage;

export const isIOS = (): boolean => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

export const isAndroid = (): boolean => {
    return /android/i.test(navigator.userAgent);
};